import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Trash2, Search, Eye, ChevronLeft, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import {
  termToMultipartFields,
  updateTermMultipart,
} from "@/lib/termsApi";
import type { Term } from "../types";
import { getStatusBadge } from "../utils";
import { normalizeForCompare } from "@/lib/normalizeText";
import { TERMS_PAGE_SIZE_OPTIONS } from "@/lib/termsPageSizes";
import type { TermsPageSizeOption } from "@/lib/termsPageSizes";

const PAGE_SIZE_OPTIONS = TERMS_PAGE_SIZE_OPTIONS;

export type AdminTermsServerPagination = {
  totalCount: number;
  page: number;
  pageSize: TermsPageSizeOption;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: TermsPageSizeOption) => void;
  onSearchChange: (query: string) => void;
  onStatusFilterChange: (status: string) => void;
  search: string;
  statusFilter: string;
};

interface AdminTermsTableProps {
  terms: Term[];
  status: string;
  isLoading: boolean;
  /** API-driven pagination for Word Master (search/status/page bound in parent). */
  serverPagination?: AdminTermsServerPagination;
  showAddButton?: boolean;
  showFeatured?: boolean;
  /** Scholars and non-admins cannot delete; hides trash when false */
  showDeleteActions?: boolean;
  onApprove?: (term: Term) => void;
  onReject?: (term: Term) => void;
  onEdit: (term: Term) => void;
  onDelete: (term: Term) => void;
  onAdd?: () => void;
  onStatusChange?: (
    term: Term,
    newStatus: "pending" | "approved" | "rejected"
  ) => void;
  onRefresh?: () => void;
  /** When set, shows a leading checkbox column and header “select all” for the current filtered result set. */
  rowSelection?: {
    selectedIds: ReadonlySet<string>;
    onChange: (next: Set<string>) => void;
  };
}

export function AdminTermsTable({
  terms,
  status,
  isLoading,
  showFeatured,
  showDeleteActions = true,
  onEdit,
  onDelete,
  onStatusChange,
  onRefresh,
  rowSelection,
  serverPagination,
}: AdminTermsTableProps) {
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [localStatusFilter, setLocalStatusFilter] = useState<string>(
    status === "all" ? "all" : status
  );
  const [detailTerm, setDetailTerm] = useState<Term | null>(null);
  const [localPage, setLocalPage] = useState(1);
  // Separate display state for server-paged search so the input updates instantly
  // while the actual filter call is debounced.
  const [serverSearchInput, setServerSearchInput] = useState(
    serverPagination?.search ?? ""
  );
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [localPageSize, setLocalPageSize] = useState<number>(
    PAGE_SIZE_OPTIONS[0]
  );

  const isServerPaged = !!serverPagination;
  const searchQuery = isServerPaged
    ? serverPagination.search
    : localSearchQuery;
  const statusFilter = isServerPaged
    ? serverPagination.statusFilter
    : localStatusFilter;
  const page = isServerPaged ? serverPagination.page : localPage;
  const pageSize = isServerPaged
    ? serverPagination.pageSize
    : localPageSize;

  const isAllView = status === "all";

  const filtered = useMemo(() => {
    // Server-paged: `terms` is already the correct page of pre-filtered results
    // from the catalog/API — do not re-filter here.
    if (isServerPaged) {
      return terms;
    }
    const q = normalizeForCompare(searchQuery);
    return terms
      .filter(term => {
        if (isAllView && statusFilter !== "all") {
          return term.status === statusFilter;
        }
        if (!isAllView) {
          return term.status === status;
        }
        return true;
      })
      .filter(term => {
        if (!q) return true;
        return (
          normalizeForCompare(term.word ?? "").includes(q) ||
          normalizeForCompare(term.contributor_name ?? "").includes(q) ||
          normalizeForCompare(term.contributor_email ?? "").includes(q) ||
          normalizeForCompare(term.meaning_english ?? "").includes(q)
        );
      });
  }, [terms, isAllView, statusFilter, status, searchQuery, isServerPaged]);

  // Server-paged: the catalog already computed the filtered total; use it directly.
  const totalFiltered = isServerPaged
    ? serverPagination.totalCount
    : filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const pageEndExclusive = isServerPaged
    ? Math.min(pageStart + filtered.length, totalFiltered)
    : Math.min(pageStart + pageSize, totalFiltered);
  const paginatedTerms = isServerPaged
    ? filtered
    : filtered.slice(pageStart, pageStart + pageSize);

  const filteredIds = useMemo(() => filtered.map(t => t.id), [filtered]);

  const allFilteredSelected =
    filteredIds.length > 0 &&
    filteredIds.every(id => rowSelection?.selectedIds.has(id));
  const someFilteredSelected =
    !!rowSelection &&
    filteredIds.some(id => rowSelection.selectedIds.has(id));

  const headerSelectChecked: boolean | "indeterminate" =
    filteredIds.length === 0
      ? false
      : allFilteredSelected
        ? true
        : someFilteredSelected && !allFilteredSelected
          ? "indeterminate"
          : false;

  useEffect(() => {
    if (!isServerPaged) {
      setLocalPage(1);
    }
  }, [searchQuery, statusFilter, status, isServerPaged]);

  useEffect(() => {
    if (!isServerPaged) {
      setLocalPage(p => Math.min(p, totalPages));
    }
  }, [totalPages, isServerPaged]);

  useEffect(() => {
    if (!isServerPaged) {
      setLocalPage(1);
    }
  }, [pageSize, isServerPaged]);

  const handleSearchInput = (value: string) => {
    if (isServerPaged) {
      setServerSearchInput(value);
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        serverPagination.onSearchChange(value);
      }, 350);
    } else {
      setLocalSearchQuery(value);
    }
  };

  // Cancel pending debounce on unmount.
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const handleStatusFilter = (value: string) => {
    if (isServerPaged) {
      serverPagination.onStatusFilterChange(value);
    } else {
      setLocalStatusFilter(value);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search by word, contributor..."
            value={isServerPaged ? serverSearchInput : localSearchQuery}
            onChange={e => handleSearchInput(e.target.value)}
            className="pl-10"
          />
        </div>
        {isAllView && (
          <Tabs value={statusFilter} onValueChange={handleStatusFilter}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="pending">Pending</TabsTrigger>
              <TabsTrigger value="approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected">Rejected</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="py-12 text-center">
              <div className="border-primary mx-auto h-8 w-8 animate-spin rounded-full border-b-2" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-muted-foreground py-12 text-center">
              No terms found.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow>
                  {rowSelection ? (
                    <TableHead className="w-12 px-2">
                      <Checkbox
                        disabled={filteredIds.length === 0}
                        checked={headerSelectChecked}
                        onCheckedChange={() => {
                          if (!rowSelection || filteredIds.length === 0) return;
                          const cur = rowSelection.selectedIds;
                          if (allFilteredSelected) {
                            const next = new Set(cur);
                            for (const id of filteredIds) next.delete(id);
                            rowSelection.onChange(next);
                          } else {
                            rowSelection.onChange(new Set([...cur, ...filteredIds]));
                          }
                        }}
                        aria-label="Select all filtered rows"
                      />
                    </TableHead>
                  ) : null}
                  <TableHead>Word</TableHead>
                  <TableHead className="hidden md:table-cell">
                    English Meaning
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">
                    Contributor
                  </TableHead>
                  <TableHead className="hidden sm:table-cell">Date</TableHead>
                  <TableHead className="w-[100px]">Visible</TableHead>
                  {showFeatured && <TableHead>Featured</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedTerms.map(term => (
                  <TableRow
                    key={term.id}
                    className="hover:bg-secondary/30 cursor-pointer"
                    onClick={() => setDetailTerm(term)}
                  >
                    {rowSelection ? (
                      <TableCell
                        className="w-12 px-2"
                        onClick={e => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={rowSelection.selectedIds.has(term.id)}
                          onCheckedChange={c => {
                            const next = new Set(rowSelection.selectedIds);
                            if (c === true) next.add(term.id);
                            else next.delete(term.id);
                            rowSelection.onChange(next);
                          }}
                          aria-label={`Select ${term.word}`}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell className="max-w-[12rem] break-words font-medium md:max-w-none">
                      <div>{term.word}</div>
                      {(term.word_gujarati || term.word_devanagari || term.word_hindi) && (
                        <div className="font-devanagari mt-0.5 flex flex-wrap gap-x-2 text-xs font-normal text-muted-foreground">
                          {term.word_gujarati && <span>{term.word_gujarati}</span>}
                          {term.word_devanagari && <span>{term.word_devanagari}</span>}
                          {term.word_hindi && <span>{term.word_hindi}</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden max-w-[200px] truncate md:table-cell">
                      {term.meaning_english || "-"}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden text-sm sm:table-cell">
                      <div>{term.contributor_name || "Anonymous"}</div>
                      {term.contributor_email && (
                        <div className="text-muted-foreground text-xs">
                          {term.contributor_email}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground hidden text-sm sm:table-cell">
                      {new Date(term.created_at).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "2-digit",
                      })}
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Switch
                        checked={
                          term.status === "approved" && term.is_enabled
                        }
                        disabled={
                          term.status === "rejected" ||
                          term.status === "pending"
                        }
                        onCheckedChange={async checked => {
                          const next = !!checked;
                          if (term.status !== "approved") {
                            if (next) {
                              if (term.status === "rejected") {
                                toast.info("The word is rejected.");
                              } else {
                                toast.info(
                                  "Approve this term before it can appear on the site."
                                );
                              }
                            }
                            return;
                          }
                          const { ok, error } = await updateTermMultipart(
                            term.id,
                            {
                              ...termToMultipartFields(term),
                              is_enabled: next,
                              admin_notes: term.admin_notes ?? "",
                            }
                          );
                          if (!ok || error) {
                            toastApiError(error || "Failed to update visibility");
                            return;
                          }
                          toast.success(
                            next
                              ? `"${term.word}" is visible on the site`
                              : `"${term.word}" is hidden from the site`
                          );
                          onRefresh?.();
                        }}
                        title={
                          term.status === "rejected"
                            ? "Rejected terms cannot be shown on the site"
                            : term.status === "pending"
                              ? "Only approved terms can appear on the site"
                              : undefined
                        }
                        className={
                          term.status === "rejected" ||
                          term.status === "pending"
                            ? "opacity-60"
                            : undefined
                        }
                        aria-label={
                          term.status === "rejected"
                            ? `Visibility locked: "${term.word}" is rejected`
                            : term.status === "pending"
                              ? `Visibility locked until approved: "${term.word}"`
                              : term.is_enabled
                                ? `Hide "${term.word}" from site`
                                : `Show "${term.word}" on site`
                        }
                      />
                    </TableCell>
                    {showFeatured && (
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={term.featured}
                          disabled={term.status === "rejected"}
                          title={
                            term.status === "rejected"
                              ? "Rejected terms cannot be featured"
                              : undefined
                          }
                          className={
                            term.status === "rejected"
                              ? "opacity-60"
                              : undefined
                          }
                          aria-label={
                            term.status === "rejected"
                              ? `Featured locked: "${term.word}" is rejected`
                              : term.featured
                                ? `Unfeature "${term.word}"`
                                : `Feature "${term.word}"`
                          }
                          onCheckedChange={async checked => {
                            const next = !!checked;
                            const { ok, error } = await updateTermMultipart(
                              term.id,
                              {
                                ...termToMultipartFields(term),
                                is_featured: next,
                                admin_notes: term.admin_notes ?? "",
                              }
                            );
                            if (!ok || error) {
                              toastApiError(error || "Failed to update featured");
                              return;
                            }
                            toast.success(
                              next
                                ? `"${term.word}" featured`
                                : `"${term.word}" unfeatured`
                            );
                            onRefresh?.();
                          }}
                        />
                      </TableCell>
                    )}
                    <TableCell>{getStatusBadge(term.status)}</TableCell>
                    <TableCell
                      className="text-right"
                      onClick={e => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDetailTerm(term)}
                          className="h-8 w-8"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(term)}
                          className="h-8 w-8"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {showDeleteActions ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDelete(term)}
                            className="text-destructive hover:text-destructive h-8 w-8"
                            title="Delete term"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
          {!isLoading && filtered.length > 0 ? (
            <div className="border-border flex flex-col gap-3 border-t px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <p className="text-muted-foreground text-sm tabular-nums">
                Showing{" "}
                <span className="text-foreground font-medium">
                  {pageStart + 1}
                </span>
                –
                <span className="text-foreground font-medium">
                  {pageEndExclusive}
                </span>
                {" of "}
                <span className="text-foreground font-medium">
                  {totalFiltered}
                </span>
              </p>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="admin-terms-page-size"
                    className="text-muted-foreground shrink-0 text-sm whitespace-nowrap"
                  >
                    Per page
                  </Label>
                  <Select
                    value={String(pageSize)}
                    onValueChange={v => {
                      const size = Number(v) as TermsPageSizeOption;
                      if (isServerPaged) {
                        serverPagination.onPageSizeChange(size);
                      } else {
                        setLocalPageSize(size);
                      }
                    }}
                  >
                    <SelectTrigger
                      id="admin-terms-page-size"
                      className="h-9 w-[4.75rem]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZE_OPTIONS.map(n => (
                        <SelectItem key={n} value={String(n)}>
                          {n}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1"
                    disabled={safePage <= 1}
                    onClick={() => {
                      const next = Math.max(1, safePage - 1);
                      if (isServerPaged) {
                        serverPagination.onPageChange(next);
                      } else {
                        setLocalPage(next);
                      }
                    }}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-muted-foreground px-1 text-sm tabular-nums">
                    Page {safePage} of {totalPages}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-9 gap-1"
                    disabled={safePage >= totalPages}
                    onClick={() => {
                      const next = Math.min(totalPages, safePage + 1);
                      if (isServerPaged) {
                        serverPagination.onPageChange(next);
                      } else {
                        setLocalPage(next);
                      }
                    }}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      {detailTerm && (
        <Dialog open={!!detailTerm} onOpenChange={() => setDetailTerm(null)}>
          <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {detailTerm.word}
              </DialogTitle>
              {(detailTerm.word_gujarati || detailTerm.word_devanagari || detailTerm.word_hindi) && (
                <div className="font-devanagari flex flex-wrap gap-3 text-base text-muted-foreground">
                  {detailTerm.word_gujarati && <span>{detailTerm.word_gujarati}</span>}
                  {detailTerm.word_devanagari && <span>{detailTerm.word_devanagari}</span>}
                  {detailTerm.word_hindi && <span>{detailTerm.word_hindi}</span>}
                </div>
              )}
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <span className="text-muted-foreground text-sm font-medium">
                  Current Status:
                </span>
                {getStatusBadge(detailTerm.status)}
              </div>

              {onStatusChange && (
                <div className="flex items-center gap-3">
                  <Label className="text-sm font-medium">Change Status:</Label>
                  <Select
                    value={detailTerm.status}
                    onValueChange={val => {
                      onStatusChange(
                        detailTerm,
                        val as "pending" | "approved" | "rejected"
                      );
                      setDetailTerm({ ...detailTerm, status: val as any });
                    }}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-muted-foreground text-xs">
                    English Meaning
                  </Label>
                  <p className="bg-secondary/30 mt-1 min-h-[60px] rounded-lg p-3 text-sm">
                    {detailTerm.meaning_english || "Not provided"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">
                    Hindi Meaning
                  </Label>
                  <p className="bg-secondary/30 font-devanagari mt-1 min-h-[60px] rounded-lg p-3 text-sm">
                    {detailTerm.meaning_hindi || "Not provided"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">
                    Gujarati Meaning
                  </Label>
                  <p className="bg-secondary/30 font-devanagari mt-1 min-h-[60px] rounded-lg p-3 text-sm">
                    {detailTerm.meaning_gujarati || "Not provided"}
                  </p>
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">
                    Pronunciation
                  </Label>
                  <p className="bg-secondary/30 mt-1 min-h-[60px] rounded-lg p-3 text-sm">
                    {detailTerm.pronunciation || "Not provided"}
                  </p>
                </div>
              </div>

              {detailTerm.example_usage && (
                <div>
                  <Label className="text-muted-foreground text-xs">
                    Example Usage
                  </Label>
                  <p className="bg-secondary/30 mt-1 rounded-lg p-3 text-sm">
                    {detailTerm.example_usage}
                  </p>
                </div>
              )}

              <div className="border-t pt-4">
                <Label className="text-muted-foreground text-xs">
                  Contributor
                </Label>
                <div className="mt-1 flex items-center gap-4">
                  <span className="text-sm font-medium">
                    {detailTerm.contributor_name || "Anonymous"}
                  </span>
                  {detailTerm.contributor_email && (
                    <a
                      href={`mailto:${detailTerm.contributor_email}`}
                      className="text-primary text-sm hover:underline"
                    >
                      {detailTerm.contributor_email}
                    </a>
                  )}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Submitted:{" "}
                  {new Date(detailTerm.created_at).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>

              {detailTerm.admin_notes && (
                <div>
                  <Label className="text-muted-foreground text-xs">
                    Admin Notes
                  </Label>
                  <p className="mt-1 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm">
                    {detailTerm.admin_notes}
                  </p>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  onEdit(detailTerm);
                  setDetailTerm(null);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" /> Edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
